// src/components/TherapistRegister.js - Dedicated therapist registration component
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Icon from './Icon';
import DatePicker from './DatePicker';
import EmailVerificationForm from './EmailVerificationForm';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:8000';

function TherapistRegister({ onBackToLogin, onRegistrationSuccess, onLogin, returnTo }) {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [showEmailVerification, setShowEmailVerification] = useState(false);
  const [registeredEmail, setRegisteredEmail] = useState('');
  const [formData, setFormData] = useState({
    role: 'therapist',
    email: '',
    password: '',
    confirmPassword: '',
    first_name: '',
    last_name: '',
    phone: '',
    date_of_birth: '',
    // Therapist-specific fields
    clinic_name: '',
    education: '',
    bio: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const validateStep1 = () => {
    const { email, password, confirmPassword } = formData;
    
    if (!email || !password || !confirmPassword) {
      setError('Alla fält är obligatoriska');
      return false;
    }
    
    if (password !== confirmPassword) {
      setError('Lösenorden matchar inte');
      return false;
    }
    
    if (password.length < 8) {
      setError('Lösenordet måste vara minst 8 tecken');
      return false;
    }
    
    return true;
  };

  const validateStep2 = () => {
    const { first_name, last_name } = formData;
    
    if (!first_name || !last_name) {
      setError('Förnamn och efternamn är obligatoriska');
      return false;
    }
    
    return true;
  };

  const validateStep3 = () => {
    // Step 3 is confirmation, no additional validation needed
    return true;
  };

  const nextStep = () => {
    setError('');
    
    if (currentStep === 1 && !validateStep1()) return;
    if (currentStep === 2 && !validateStep2()) return;
    
    setCurrentStep(prev => prev + 1);
  };

  const prevStep = () => {
    setError('');
    setCurrentStep(prev => prev - 1);
  };

  const handleSubmit = async () => {
    if (!validateStep3()) return;
    
    setLoading(true);
    setError('');

    try {
      const payload = {
        ...formData,
        name: `${formData.first_name} ${formData.last_name}`
      };
      delete payload.confirmPassword;

      // Add simplified therapist-specific fields only if role is therapist
      if (formData.role === 'therapist') {
        payload.clinic_name = formData.clinic_name || null;
        payload.education = formData.education || null;
        payload.bio = formData.bio || null;
      }

      // Clean up empty string values
      Object.keys(payload).forEach(key => {
        if (payload[key] === '') {
          payload[key] = null;
        }
      });

      console.log('🔄 Submitting registration data:', payload);

      const response = await axios.post(`${API_BASE}/auth/register`, payload);
      
      console.log('✅ Registration successful:', response.data);
      
      // Store registered email for verification
      setRegisteredEmail(formData.email);
      
      // Show email verification form instead of redirecting immediately
      setShowEmailVerification(true);
      
    } catch (err) {
      console.error('❌ Registration error:', err);
      console.error('❌ Error response:', err.response?.data);
      
      // Handle different error formats
      let errorMessage = 'Registrering misslyckades';
      
      if (err.response?.data) {
        const errorData = err.response.data;
        
        // Handle FastAPI validation errors
        if (Array.isArray(errorData.detail)) {
          const validationErrors = errorData.detail.map(error => {
            const field = error.loc?.[error.loc.length - 1] || 'field';
            return `${field}: ${error.msg}`;
          });
          errorMessage = validationErrors.join(', ');
        } 
        // Handle string error messages
        else if (typeof errorData.detail === 'string') {
          errorMessage = errorData.detail;
        }
        // Handle object error messages
        else if (typeof errorData === 'string') {
          errorMessage = errorData;
        }
        // Handle other formats
        else {
          errorMessage = JSON.stringify(errorData);
        }
      } else if (err.message) {
        errorMessage = err.message;
      }
      
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleVerificationSuccess = async () => {
    console.log('🚀 Email verification successful, attempting auto-login');
    console.log('📧 Email:', registeredEmail);
    console.log('🔄 Return to:', returnTo);
    
    try {
      // Attempt auto-login after email verification
      const loginPayload = new URLSearchParams({
        username: registeredEmail,
        password: formData.password
      });
      
      console.log('� Attempting auto-login for therapist...');
      
      const loginResponse = await axios.post(`${API_BASE}/auth/token`, loginPayload, {
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' }
      });

      if (loginResponse.data.access_token) {
        console.log('✅ Auto-login successful after email verification');
        
        // Call the parent's login handler to set token and user state
        if (typeof onLogin === 'function') {
          console.log('🔄 Calling onLogin handler with token');
          onLogin(loginResponse.data.access_token, null, '/therapist');
        } else {
          // Fallback: Store token and navigate manually
          localStorage.setItem('therapy_auth_token', loginResponse.data.access_token);
          navigate('/therapist', { 
            replace: true,
            state: { 
              message: 'Välkommen! Din registrering och e-postverifiering är nu klar.',
              justRegistered: true
            }
          });
        }
        
        return;
      }
    } catch (loginError) {
      console.error('❌ Auto-login failed after verification:', loginError);
      // Fall back to manual login flow
    }
    
    // Fallback: redirect to login page with verification message
    navigate('/login', { 
      replace: true,
      state: { 
        message: 'E-post verifierad! Du kan nu logga in.',
        email: registeredEmail,
        returnTo: returnTo || '/therapist'
      } 
    });
  };

  // Show email verification form if registration was successful
  if (showEmailVerification) {
    return (
      <EmailVerificationForm
        email={registeredEmail}
        onVerificationSuccess={handleVerificationSuccess}
        onBackToLogin={onBackToLogin}
      />
    );
  }

  return (
    <div className="max-w-2xl mx-auto my-8 p-8 border border-gray-300 rounded-lg bg-white shadow-lg">
      <h2 className="text-2xl font-bold text-center mb-6 text-gray-900">Registrera dig som terapeut</h2>
      
      {/* Patient redirect notice */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
        <div className="flex items-start space-x-3">
          <svg className="w-5 h-5 text-blue-500 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <div>
            <h4 className="text-sm font-semibold text-blue-900 mb-1">Är du patient/klient?</h4>
            <p className="text-sm text-blue-800 mb-3">
              Som patient registrerar du dig via <strong>Direktchat</strong> eller använder en <strong>terapeutinbjudan</strong>. 
              Denna sida är endast för terapeuter.
            </p>
            <div className="flex gap-2">
              <button
                onClick={() => window.location.href = '/instant-chat'}
                className="text-xs bg-blue-600 text-white px-3 py-1 rounded hover:bg-blue-700 transition-colors"
              >
                → Börja direktchat
              </button>
              <button
                onClick={() => window.location.href = '/login'}
                className="text-xs bg-gray-600 text-white px-3 py-1 rounded hover:bg-gray-700 transition-colors"
              >
                → Logga in
              </button>
            </div>
          </div>
        </div>
      </div>
      
      {/* Invitation Context Banner */}
      {returnTo && returnTo.includes('/invite/') && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
          <div className="flex items-start space-x-3">
            <svg className="w-5 h-5 text-blue-500 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <div>
              <h3 className="text-sm font-semibold text-blue-900 mb-1">🎯 Terapeutinbjudan</h3>
              <p className="text-sm text-blue-800">
                Du registrerar dig för att använda din terapeutinbjudan. Efter registrering kommer du automatiskt att anslutas till din terapeut.
              </p>
            </div>
          </div>
        </div>
      )}
      
      {/* Step Indicator - 3 steps for therapist registration */}
      <div className="flex mb-8 border-b border-gray-200">
        {[1, 2, 3].map((step) => (
          <div
            key={step}
            className={`flex-1 pb-3 text-center border-b-2 transition-colors ${
              step <= currentStep
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-400'
            }`}
          >
            <div className="font-medium text-sm">
              {step === 1 && 'Kontoinformation'}
              {step === 2 && 'Professionell information'}
              {step === 3 && 'Bekräfta'}
            </div>
          </div>
        ))}
      </div>

      {error && (
        <div className="bg-red-100 text-red-800 p-3 rounded border border-red-300 text-sm mb-6">
          <div className="font-medium mb-1">Registrering misslyckades:</div>
          <div className="text-sm">
            {typeof error === 'string' ? error : 'Ett oväntat fel uppstod. Försök igen.'}
          </div>
        </div>
      )}

      {/* Step 1: Account Information */}
      {currentStep === 1 && (
        <div className="space-y-6">
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Kontoinformation</h3>
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
              <div className="flex items-start space-x-3">
                <svg className="w-5 h-5 text-blue-500 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <div>
                  <h4 className="text-sm font-semibold text-blue-900 mb-1">Terapeut registrering</h4>
                  <p className="text-sm text-blue-800">
                    Som terapeut får du tillgång till verktyg för klienthantering, AI-assisterad analys och säkra kommunikationskanaler.
                  </p>
                </div>
              </div>
            </div>
          </div>
          
          <div className="space-y-4">
            <div className="space-y-1">
              <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                E-postadress *
              </label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="din@email.com"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-1">
                <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                  Lösenord *
                </label>
                <input
                  type="password"
                  id="password"
                  name="password"
                  value={formData.password}
                  onChange={handleInputChange}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Minst 8 tecken"
                />
              </div>

              <div className="space-y-1">
                <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700">
                  Bekräfta lösenord *
                </label>
                <input
                  type="password"
                  id="confirmPassword"
                  name="confirmPassword"
                  value={formData.confirmPassword}
                  onChange={handleInputChange}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Upprepa lösenord"
                />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Step 2: Professional Information */}
      {currentStep === 2 && (
        <div className="space-y-4">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Professionell information</h3>
          
          {/* Personal name fields */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-1">
              <label htmlFor="first_name" className="block text-sm font-medium text-gray-700">
                Förnamn *
              </label>
              <input
                type="text"
                id="first_name"
                name="first_name"
                value={formData.first_name}
                onChange={handleInputChange}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Ditt förnamn"
              />
            </div>

            <div className="space-y-1">
              <label htmlFor="last_name" className="block text-sm font-medium text-gray-700">
                Efternamn
              </label>
              <input
                type="text"
                id="last_name"
                name="last_name"
                value={formData.last_name}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Ditt efternamn"
              />
            </div>
          </div>

          <div className="space-y-1">
            <label htmlFor="phone" className="block text-sm font-medium text-gray-700">
              Telefonnummer (valfritt)
            </label>
            <input
              type="tel"
              id="phone"
              name="phone"
              value={formData.phone}
              onChange={handleInputChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="+46 70 123 45 67"
            />
          </div>

          {/* Professional fields */}
          <div className="space-y-4">
            <div className="space-y-1">
              <label htmlFor="clinic_name" className="block text-sm font-medium text-gray-700">
                Klinik/Arbetsplats (valfritt)
              </label>
              <input
                type="text"
                id="clinic_name"
                name="clinic_name"
                value={formData.clinic_name}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Namn på din klinik eller arbetsplats"
              />
            </div>

            <div className="space-y-1">
              <label htmlFor="education" className="block text-sm font-medium text-gray-700">
                Utbildning (valfritt)
              </label>
              <input
                type="text"
                id="education"
                name="education"
                value={formData.education}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="t.ex. Psykolog, Legitimerad psykoterapeut"
              />
            </div>

            <div className="space-y-1">
              <label htmlFor="bio" className="block text-sm font-medium text-gray-700">
                Kort presentation (valfritt)
              </label>
              <textarea
                id="bio"
                name="bio"
                value={formData.bio}
                onChange={handleInputChange}
                rows="3"
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                placeholder="Beskriv kort din behandlingsfilosofi och approach..."
              />
            </div>
          </div>
        </div>
      )}

      {/* Step 3: Confirmation */}
      {currentStep === 3 && (
        <div className="space-y-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Bekräfta din information</h3>
          
          <div className="bg-gray-50 p-6 rounded-lg space-y-4">
            <div>
              <div className="text-sm font-medium text-gray-700">Roll</div>
              <div className="text-gray-900 flex items-center gap-2">
                <Icon 
                  name="message-circle-heart"
                  size={16} 
                  className="text-red-600"
                />
                Terapeut
              </div>
            </div>
            
            <div>
              <div className="text-sm font-medium text-gray-700">Namn</div>
              <div className="text-gray-900">{formData.first_name} {formData.last_name}</div>
            </div>
            
            <div>
              <div className="text-sm font-medium text-gray-700">E-post</div>
              <div className="text-gray-900">{formData.email}</div>
            </div>
            
            {formData.phone && (
              <div>
                <div className="text-sm font-medium text-gray-700">Telefon</div>
                <div className="text-gray-900">{formData.phone}</div>
              </div>
            )}
            
            <div className="space-y-2">
              {formData.clinic_name && (
                <div>
                  <div className="text-sm font-medium text-gray-700">Klinik/Arbetsplats</div>
                  <div className="text-gray-900">{formData.clinic_name}</div>
                </div>
              )}
              
              {formData.education && (
                <div>
                  <div className="text-sm font-medium text-gray-700">Utbildning</div>
                  <div className="text-gray-900">{formData.education}</div>
                </div>
              )}
            </div>
            
            {formData.bio && (
              <div>
                <div className="text-sm font-medium text-gray-700">Presentation</div>
                <div className="text-gray-900 text-sm italic">"{formData.bio}"</div>
              </div>
            )}
          </div>
          
          <div className="text-sm text-gray-600 bg-blue-50 p-4 rounded-lg">
            <Icon name="shield" size={16} className="inline mr-2 text-blue-600" />
            Din information hanteras säkert enligt GDPR och vår integritetspolicy.
          </div>
        </div>
      )}

      {/* Navigation Buttons */}
      <div className="flex justify-between mt-8">
        {currentStep > 1 ? (
          <button
            type="button"
            onClick={prevStep}
            className="px-6 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition-colors font-medium"
          >
            Tillbaka
          </button>
        ) : (
          <button
            type="button"
            onClick={onBackToLogin}
            className="px-6 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition-colors font-medium"
          >
            Tillbaka till inloggning
          </button>
        )}

        {currentStep < 3 ? (
          <button
            type="button"
            onClick={nextStep}
            className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors font-medium"
          >
            Nästa
          </button>
        ) : (
          <button
            type="button"
            onClick={handleSubmit}
            disabled={loading}
            className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 transition-colors font-medium"
          >
            {loading ? 'Skapa konto...' : 'Skapa terapeut-konto'}
          </button>
        )}
      </div>
    </div>
  );
}

export default TherapistRegister;